import React, { useCallback, useRef } from 'react'
import styles from './addPlayerSearchResults.module.scss'
import LoadMore from 'components/generic/LoadMore'
import i18n from 'constants/strings'
import NoDataContainer from 'components/generic/NoDataContainer'
import { PostScoreModalId } from 'constants/modals'
import SearchResultsTableDesktop from 'components/search/SearchResultsTableDesktop'
import SearchResultMobile from 'components/search/SearchResultMobile'
import usePlayerSearchWithSorting from 'hooks/use-player-search-with-sorting'
import SearchResultsCounter from 'components/search/SearchResultsCounter'
import LoadingSpinnerWithContainer from 'components/generic/LoadingSpinnerWithContainer'
import { Player, PublicPersonFilterOptions } from 'types/global'
import SearchResult from 'components/search/SearchResultDesktop'
import BtnPrimary from 'components/generic/BtnPrimary'
import BtnSecondary from 'components/generic/BtnSecondary'
import { PLAYERS_FETCH_SIZE_LOGGED_IN } from 'constants/global'

interface Props {
  selectedPlayersIdsMap: Record<string, boolean>
  onAddPlayer?: (player: any) => void
  showMobileLayout: boolean
}

const AddPlayerSearchResults: React.FC<Props> = ({
  selectedPlayersIdsMap,
  onAddPlayer,
  showMobileLayout,
}) => {
  const searchFilterOptions = useRef<PublicPersonFilterOptions>({ scopeForSelfSubmission: true })
  const {
    loadedPlayers,
    totalPlayers,
    isSearchPending,
    sorting,
    loadMorePlayers,
    allPlayersLoaded,
    isLoadingMorePlayers,
    handleTableSorting,
  } = usePlayerSearchWithSorting(PostScoreModalId.ADD_PLAYER, {
    filterOptions: searchFilterOptions.current,
    pageSize: PLAYERS_FETCH_SIZE_LOGGED_IN,
  })
  const renderTableRow = useCallback(
    (p: Player) => {
      const isAlreadySelected = selectedPlayersIdsMap[p.id]
      return (
        <SearchResult
          key={p.id}
          player={p}
          className={isAlreadySelected ? styles.alreadySelected : undefined}
        >
          <td className={styles.cellAddPlayerBtn}>
            {isAlreadySelected ? (
              <p className={styles.selectedText}>
                {i18n.t('strSocialMatches.modals.addPlayer.added')}
              </p>
            ) : (
              <BtnPrimary
                size='small'
                onClick={() => onAddPlayer?.(p)}
                border={false}
                className={styles.addPlayerBtn}
              >
                {i18n.t('strSocialMatches.modals.addPlayer.addPlayer')}
              </BtnPrimary>
            )}
          </td>
        </SearchResult>
      )
    },
    [onAddPlayer, selectedPlayersIdsMap]
  )

  return (
    <section>
      <SearchResultsCounter
        loadedResults={loadedPlayers?.length}
        totalResults={totalPlayers}
        isLoading={!totalPlayers && isSearchPending}
        withDivider
      />
      {(isLoadingMorePlayers && !loadedPlayers?.length) ||
      (isSearchPending && !loadedPlayers?.length) ? (
        <LoadingSpinnerWithContainer containerHeight={500} spinnerWidth={90} />
      ) : loadedPlayers && loadedPlayers.length > 0 ? (
        showMobileLayout ? (
          <div className={styles.mobileResultsContainer}>
            <ul className={styles.mobileResultsList}>
              {loadedPlayers.map(p => {
                const isAlreadySelected = selectedPlayersIdsMap[p.id]

                return (
                  <li key={p.id}>
                    <SearchResultMobile player={p} className={styles.mobileResult}>
                      <div className={styles.cellAddPlayerBtnMobile}>
                        {isAlreadySelected ? (
                          <p className={styles.selectedText}>
                            {i18n.t('strSocialMatches.modals.addPlayer.added')}
                          </p>
                        ) : (
                          <BtnSecondary
                            size='small'
                            onClick={() => onAddPlayer?.(p)}
                            fullWidthAtMobile
                          >
                            {i18n.t('strSocialMatches.modals.addPlayer.addPlayer')}
                          </BtnSecondary>
                        )}
                      </div>
                    </SearchResultMobile>
                  </li>
                )
              })}
            </ul>
            {!allPlayersLoaded && (
              <div className={styles.btnWrapper}>
                <LoadMore
                  loading={isLoadingMorePlayers}
                  loadMore={loadMorePlayers}
                  text={i18n.t('searchResults.showMore')}
                />
              </div>
            )}
          </div>
        ) : (
          <div>
            <div className={styles.resultsTableContainer}>
              <SearchResultsTableDesktop
                players={loadedPlayers}
                sorting={sorting}
                handleSelectSorting={handleTableSorting}
                renderTableRow={renderTableRow}
                classHiddenCell={styles.cellAddPlayerBtn}
                resizeHeadersRow={true}
              />
            </div>
            {!allPlayersLoaded && (
              <div className={styles.btnWrapper}>
                <LoadMore
                  loading={isLoadingMorePlayers}
                  loadMore={loadMorePlayers}
                  text={i18n.t('searchResults.loadMore')}
                />
              </div>
            )}
          </div>
        )
      ) : (
        <NoDataContainer>
          <p className={styles.noDataText}>{i18n.t('searchResults.noPlayers')}</p>
        </NoDataContainer>
      )}
    </section>
  )
}

export default AddPlayerSearchResults
